import { useState, useEffect, useCallback, useRef } from "react";
import { Button, Checkbox, Modal } from "@legal-500/legal500-design-system";
import { contacts as contactsService } from "@/api/contacts.service";
import { approvalRequests as approvalRequestsService } from "@/api/approvalRequests.service";
import type { ApprovalRequest } from "@/api/approvalRequests.service";
import { LinkContactConfirm } from "./LinkContactConfirm";
import { needsNameConfirmation } from "./contactNameMatch";
import styles from "./LinkContactModal.module.css";

type ContactResult = {
  contactId: number;
  name: string;
  companyId: number | null;
  companyName: string | null;
};

type Props = {
  item: ApprovalRequest;
  onClose: () => void;
  onLinked: () => void;
};

export function LinkContactModal({ item, onClose, onLinked }: Props) {
  const [searchName, setSearchName] = useState(item.lawyerName ?? "");
  const [withinFirm, setWithinFirm] = useState(item.firmRef != null);
  const [results, setResults] = useState<ContactResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [pendingContact, setPendingContact] = useState<ContactResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initialSearchDone = useRef(false);
  const currentContactId = item.contactId;
  const isChange = currentContactId != null;

  const search = useCallback(
    async (name: string, scopedToFirm: boolean) => {
      if (!name.trim()) {
        setResults([]);
        return;
      }
      setSearching(true);
      setError(null);
      try {
        const firmRef = scopedToFirm && item.firmRef ? item.firmRef : undefined;
        const resp = await contactsService.getByName({ name, firmRef });
        setResults(resp.contacts);
      } catch {
        setError("Failed to search contacts. Please try again.");
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [item.firmRef],
  );

  useEffect(() => {
    if (initialSearchDone.current) return;
    initialSearchDone.current = true;
    search(searchName, withinFirm);
  }, []);

  const handleNameChange = (value: string) => {
    setSearchName(value);
    search(value, withinFirm);
  };

  const handleWithinFirmChange = (checked: boolean) => {
    setWithinFirm(checked);
    search(searchName, checked);
  };

  const handleLink = async (contact: ContactResult) => {
    setLinkingId(contact.contactId);
    setError(null);
    try {
      await approvalRequestsService.updateContactId(item.id, contact.contactId);
      onLinked();
    } catch {
      setError("Failed to link contact. Please try again.");
      setLinkingId(null);
    }
  };

  const requestLink = (contact: ContactResult) => {
    if (needsNameConfirmation(item.lawyerName, contact.name)) {
      setError(null);
      setPendingContact(contact);
      return;
    }
    handleLink(contact);
  };

  const handleBack = () => {
    setPendingContact(null);
    setError(null);
  };

  const linkLabel = (contact: ContactResult) => {
    if (contact.contactId === currentContactId) return "Linked";
    return linkingId === contact.contactId ? "Linking..." : "Link";
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isChange ? "Change Linked Contact" : "Link Contact"}
      size="medium"
      blurBackground
    >
      {pendingContact ? (
        <LinkContactConfirm
          lawyerName={item.lawyerName ?? ""}
          contactName={pendingContact.name}
          companyName={pendingContact.companyName}
          linking={linkingId != null}
          error={error}
          onBack={handleBack}
          onConfirm={() => handleLink(pendingContact)}
        />
      ) : (
        <div className={styles.body}>
          {isChange && <p className={styles.hint}>Currently linked: {currentContactId}</p>}

          <div className={styles.searchRow}>
            <input
              className={styles.searchInput}
              type="text"
              value={searchName}
              onChange={e => handleNameChange(e.target.value)}
              placeholder="Search by name..."
              aria-label="Contact name"
            />
            <Checkbox
              checked={withinFirm}
              disabled={item.firmRef == null}
              onChange={e => handleWithinFirmChange(e.target.checked)}
              label="Search within firm only"
            />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.results}>
            {searching && <p className={styles.hint}>Searching...</p>}
            {!searching && results.length === 0 && searchName.trim() && (
              <p className={styles.hint}>No contacts found.</p>
            )}
            {!searching && results.length > 0 && (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {results.map(contact => (
                    <tr key={contact.contactId}>
                      <td>{contact.name}</td>
                      <td>{contact.companyName ?? "—"}</td>
                      <td>
                        <Button
                          variant="outlined"
                          color="default"
                          size="small"
                          disabled={linkingId != null || contact.contactId === currentContactId}
                          onClick={() => requestLink(contact)}
                        >
                          {linkLabel(contact)}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className={styles.footer}>
            <Button
              variant="outlined"
              color="default"
              onClick={onClose}
              disabled={linkingId != null}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
